import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import type { EventTimelineItem } from 'lib/designSystem/Charts/EventTimeline';
import EventTimeline from 'lib/designSystem/Charts/EventTimeline';
import PageHeader from 'lib/designSystem/PageHeader';
import { formatDate, getShortString } from 'lib/functions/utils';
import React, { useMemo } from 'react';

type CessionHistoryDialogProps = {
  open: boolean;
  onClose: () => void;
  cessions: {
    giver: string;
    cessionary: string;
    cessionDate: string;
    cessionaryIdentifier: string;
    giverIdentifier: string;
  }[];
};

function CessionHistoryDialog({
  open,
  onClose,
  cessions,
}: CessionHistoryDialogProps) {
  const events: EventTimelineItem[] = useMemo(() => {
    if (cessions.length === 0) return [];
    const firstItem: EventTimelineItem = {
      label: 'Origen',
      subtitle: getShortString(cessions[0].giver, 35),
      valueLabel: '',
      variant: 'primary' as const,
      key: 0,
    };
    const cessionaryItems: EventTimelineItem[] = cessions.map(
      (cession, index) => ({
        label: `Cedida el ${formatDate(new Date(cession.cessionDate), 'large')} a`,
        subtitle: getShortString(cession.cessionary, 35),
        valueLabel: '',
        variant:
          index === cessions.length - 1
            ? ('success' as const)
            : ('primary' as const),
        key: index + 1,
      }),
    );
    return [firstItem, ...cessionaryItems];
  }, [cessions]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <PageHeader.TitleOnly
          iconType="history"
          title="Historial de cesiones"
          subtitle={`${cessions.length} cesi${cessions.length > 1 ? 'ones' : 'ón'}`}
          variant="dialogHeader"
        />
      </DialogTitle>
      <DialogContent>
        {cessions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No hay cesiones registradas.
          </Typography>
        ) : (
          <Box sx={{ py: 0, px: 2, maxHeight: 400, overflow: 'auto' }}>
            <EventTimeline events={events} position="right" size="small" />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CessionHistoryDialog;
